import { useState, useEffect } from 'react';
import { getBookingCommissions } from '../../lib/bookingCommissions';
import { supabase } from '../../lib/supabase';

interface BookingProfitLossProps {
  revenue: number;
  materialCosts: number;
  laborCosts: number;
  discounts?: number;
  otherCosts?: number;
  bookingId?: string;
  flatRates?: any[]; // Add flat rates data for detailed breakdown
}

export default function BookingProfitLoss({
  revenue,
  materialCosts,
  laborCosts,
  discounts = 0,
  otherCosts = 0,
  bookingId,
  flatRates = []
}: BookingProfitLossProps) {
  const [commissionCosts, setCommissionCosts] = useState(0);
  const [isLoadingCommissions, setIsLoadingCommissions] = useState(false);
  const [bonusCosts, setBonusCosts] = useState(0);
  const [isLoadingBonuses, setIsLoadingBonuses] = useState(false);
  const [timerLogs, setTimerLogs] = useState<any[]>([]);
  const [isLoadingTimerLogs, setIsLoadingTimerLogs] = useState(false);
  const [timerCosts, setTimerCosts] = useState(0);
  const [productCosts, setProductCosts] = useState(0);
  const [isLoadingProductCosts, setIsLoadingProductCosts] = useState(false);
  const [products, setProducts] = useState<any[]>([]);

  // Load commission and bonus costs when component mounts or bookingId changes
  useEffect(() => {
    const loadCommissionCosts = async () => {
      if (!bookingId) return;
      
      try {
        setIsLoadingCommissions(true);
        const commissions = await getBookingCommissions(bookingId);
        const totalCommissions = commissions.reduce((total, commission) => total + commission.commission_amount, 0);
        setCommissionCosts(totalCommissions);
      } catch (error) {
        console.error('Error loading commission costs:', error);
        setCommissionCosts(0);
      } finally {
        setIsLoadingCommissions(false);
      }
    };

    const loadBonusCosts = async () => {
      if (!bookingId || !supabase) return;
      
      try {
        setIsLoadingBonuses(true);
        console.log('🔍 BookingProfitLoss: Loading bonus costs for booking:', bookingId);
        
        // Query payroll_page table for bonus entries related to this booking
        const { data: bonusEntries, error } = await supabase
          .from('payroll_page')
          .select('bonus_earnings, total_earnings')
          .eq('booking_id', bookingId)
          .eq('entry_type', 'bonus');

        if (error) {
          console.error('🚨 BookingProfitLoss: Error fetching bonus entries:', error);
          setBonusCosts(0);
          return;
        }

        const totalBonuses = bonusEntries?.reduce((total, entry) => {
          const bonusAmount = entry.bonus_earnings || entry.total_earnings || 0;
          return total + bonusAmount;
        }, 0) || 0;

        console.log('💰 BookingProfitLoss: Found bonus entries:', bonusEntries);
        console.log('💰 BookingProfitLoss: Total bonus costs:', totalBonuses);
        setBonusCosts(totalBonuses);
      } catch (error) {
        console.error('🚨 BookingProfitLoss: Error loading bonus costs:', error);
        setBonusCosts(0);
      } finally {
        setIsLoadingBonuses(false);
      }
    };

    const loadProductCosts = async () => {
      if (!bookingId || !supabase) return;
      
      try {
        setIsLoadingProductCosts(true);
        console.log('🔍 BookingProfitLoss: Loading product costs for booking:', bookingId);
        
        // Fetch products from product_booking_sales
        const { data: bookingProducts, error: productsError } = await supabase
          .from('product_booking_sales')
          .select('*')
          .eq('booking_id', bookingId);

        if (productsError) {
          console.error('🚨 BookingProfitLoss: Error fetching booking products:', productsError);
          setProductCosts(0);
          setProducts([]);
          return;
        }

        if (!bookingProducts || bookingProducts.length === 0) {
          console.log('📦 BookingProfitLoss: No products found for this booking');
          setProductCosts(0);
          setProducts([]);
          return;
        }

        // Get unique stock item IDs
        const itemIds = bookingProducts.map(p => p.stock_item_id);
        
        // Fetch stock item details for acquisition costs
        const { data: stockItems, error: stockError } = await supabase
          .from('stock_items')
          .select('id, name, acquisition_cost')
          .in('id', itemIds);

        if (stockError) {
          console.error('🚨 BookingProfitLoss: Error fetching stock items:', stockError);
          setProductCosts(0);
          setProducts([]);
          return;
        }

        // Map stock items to booking products and calculate total cost
        const enrichedProducts = bookingProducts.map(product => {
          const stockItem = stockItems?.find(item => item.id === product.stock_item_id);
          const acquisitionCost = stockItem?.acquisition_cost || 0;
          const totalCost = acquisitionCost * product.quantity;
          
          return {
            ...product,
            stockItem,
            acquisitionCost,
            totalCost
          };
        });

        // Calculate total product costs
        const totalProductCost = enrichedProducts.reduce((total, product) => {
          return total + product.totalCost;
        }, 0);

        setProducts(enrichedProducts);
        setProductCosts(totalProductCost);
        console.log('💰 BookingProfitLoss: Total product costs:', totalProductCost);
        console.log('📦 BookingProfitLoss: Products with costs:', enrichedProducts);
      } catch (error) {
        console.error('🚨 BookingProfitLoss: Error loading product costs:', error);
        setProductCosts(0);
        setProducts([]);
      } finally {
        setIsLoadingProductCosts(false);
      }
    };

    const loadTimerLogs = async () => {
      if (!bookingId || !supabase) return;
      
      try {
        setIsLoadingTimerLogs(true);
        console.log('🔍 BookingProfitLoss: Loading timer logs for booking:', bookingId);
        
        // First test if we can access the table at all
        const { data: testData, error: testError } = await supabase
          .from('booking_timer_logs')
          .select('*')
          .limit(5);
        
        console.log('🧪 BookingProfitLoss: Test query (first 5 records):', { testData, testError });
        
        // Log the booking IDs from test data for comparison
        if (testData && testData.length > 0) {
          console.log('📝 BookingProfitLoss: Sample booking IDs in table:', testData.map(d => d.booking_id));
        }
        
        console.log('🔑 BookingProfitLoss: Booking ID we\'re searching for:', bookingId);
        console.log('🔍 BookingProfitLoss: Booking ID type:', typeof bookingId);
        
        // Now try to get timer logs for this specific booking
        const { data: logs, error } = await supabase
          .from('booking_timer_logs')
          .select('*')
          .eq('booking_id', bookingId);
        
        console.log('📊 BookingProfitLoss: Raw timer logs query result:', { logs, error });

        if (error) {
          console.error('🚨 BookingProfitLoss: Error fetching timer logs:', error);
          // If table doesn't exist, it will show in the error
          if (error.message?.includes('relation') || error.code === '42P01') {
            console.warn('⚠️ BookingProfitLoss: booking_timer_logs table may not exist yet. Run create_booking_timer_logs.sql');
          }
          setTimerLogs([]);
          setTimerCosts(0);
          return;
        }

        console.log('⏱️ BookingProfitLoss: Found timer logs:', logs);
        console.log('⏱️ BookingProfitLoss: Number of timer logs:', logs?.length || 0);
        
        // If we have logs, fetch resource details for each
        let enrichedLogs = logs || [];
        if (logs && logs.length > 0) {
          // Get unique resource IDs
          const resourceIds = [...new Set(logs.map(log => log.resource_id))];
          console.log('🔍 BookingProfitLoss: Fetching resource details for IDs:', resourceIds);
          
          // Fetch resource details
          const { data: resources, error: resourceError } = await supabase
            .from('resources')
            .select('id, first_name, last_name, hourly_rate')
            .in('id', resourceIds);
          
          console.log('👥 BookingProfitLoss: Resources fetched:', { resources, resourceError });
          
          if (resources && !resourceError) {
            // Map resources to logs
            enrichedLogs = logs.map(log => ({
              ...log,
              resource: resources.find(r => r.id === log.resource_id)
            }));
          }
        }
        
        // Calculate total timer costs based on hourly rates
        const totalTimerCost = enrichedLogs.reduce((total, log) => {
          const hourlyRate = log.resource?.hourly_rate || 0;
          const hours = log.total_seconds / 3600; // Convert seconds to hours
          const cost = hourlyRate * hours;
          console.log(`💵 Resource ${log.resource?.first_name}: ${log.total_seconds}s @ $${hourlyRate}/hr = $${cost.toFixed(2)}`);
          return total + cost;
        }, 0);

        setTimerLogs(enrichedLogs);
        setTimerCosts(totalTimerCost);
        console.log('💰 BookingProfitLoss: Total timer costs:', totalTimerCost);
        console.log('📋 BookingProfitLoss: Final enriched logs:', enrichedLogs);
      } catch (error) {
        console.error('🚨 BookingProfitLoss: Error loading timer logs:', error);
        setTimerLogs([]);
        setTimerCosts(0);
      } finally {
        setIsLoadingTimerLogs(false);
      }
    };

    loadCommissionCosts();
    loadBonusCosts();
    loadTimerLogs();
    loadProductCosts();
  }, [bookingId]);

  // Calculate resource breakdowns from flat rates
  const resourceBreakdowns = flatRates.reduce((acc: any[], rate) => {
    const resourceId = rate.resource_id || 'unassigned';
    const resourceName = rate.resource_first_name && rate.resource_last_name 
      ? `${rate.resource_first_name} ${rate.resource_last_name}`
      : 'Unassigned';
    
    const rateAmount = rate.resource_flat_rate_amount !== null && rate.resource_flat_rate_amount !== undefined
      ? Number(rate.resource_flat_rate_amount)
      : rate.resource_hourly_rate !== null && rate.resource_hourly_rate !== undefined
      ? Number(rate.resource_hourly_rate)
      : 0;
    
    const earnings = rate.hours * rateAmount;
    
    const existing = acc.find(r => r.resourceId === resourceId);
    if (existing) {
      existing.earnings += earnings;
      existing.hours += rate.hours;
    } else {
      acc.push({
        resourceId,
        resourceName,
        earnings,
        hours: rate.hours,
        rate: rateAmount
      });
    }
    
    return acc;
  }, []);

  const totalLaborCosts = laborCosts + commissionCosts + bonusCosts + timerCosts;
  const totalCosts = materialCosts + totalLaborCosts + productCosts + otherCosts + discounts;
  const profit = revenue - totalCosts;
  const profitMargin = revenue > 0 ? (profit / revenue) * 100 : 0;
  
  // Format currency to always show 2 decimal places
  const formatCurrency = (amount: number) => {
    return amount.toFixed(2);
  };

  // Format time from seconds to readable format
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-900">Profit & Loss</h3>
      </div>
      
      <div className="p-6 space-y-4">
        {/* Revenue Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-900">Revenue</span>
            <span className="text-sm font-semibold text-green-600">${formatCurrency(revenue)}</span>
          </div>
        </div>

        {/* Costs Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="mb-3">
            <span className="text-sm font-semibold text-gray-900">Costs</span>
          </div>

          {/* Labor Costs */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 font-medium">Labor Costs</span>
              <span className="text-red-600 font-bold">-${formatCurrency(totalLaborCosts)}</span>
            </div>
            
            {/* Labor Cost Breakdown */}
            {(laborCosts > 0 || commissionCosts > 0 || bonusCosts > 0 || timerCosts > 0 || isLoadingTimerLogs) && (
              <div className="ml-4 space-y-1">
                {/* Individual Resource Breakdowns */}
                {resourceBreakdowns.length > 0 && (
                  <div className="space-y-1">
                    <div className="text-xs font-medium text-gray-600 mb-1">• Flat Rate Times</div>
                    {resourceBreakdowns.map((resource, index) => (
                      <div key={index} className="flex justify-between text-xs text-gray-500 ml-4">
                        <span className="flex items-center">
                          {resource.resourceName}
                          <span className="text-gray-400 ml-1">
                            ({resource.hours}h @ ${resource.rate}/hr)
                          </span>
                        </span>
                        <span>-${formatCurrency(resource.earnings)}</span>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Timer Logs Breakdown - Always show for debugging */}
                <div className="space-y-1">
                  <div className="text-xs font-medium text-gray-600 mb-1">
                    • Timer Time
                    {isLoadingTimerLogs && ' (loading...)'}
                    {!isLoadingTimerLogs && ` (${timerLogs.length} logs)`}
                  </div>
                  {timerLogs.length === 0 && !isLoadingTimerLogs && (
                    <div className="flex justify-between text-xs text-gray-400 ml-4 italic">
                      <span>No timer logs recorded</span>
                      <span>-$0.00</span>
                    </div>
                  )}
                  {timerLogs.map((log, index) => {
                      const resourceName = log.resource?.first_name && log.resource?.last_name
                        ? `${log.resource.first_name} ${log.resource.last_name}`
                        : 'Unknown Resource';
                      const hourlyRate = log.resource?.hourly_rate || 0;
                      const hours = log.total_seconds / 3600;
                      const cost = hourlyRate * hours;
                      
                      return (
                        <div key={index} className="flex justify-between text-xs text-gray-500 ml-4">
                          <span className="flex items-center">
                            {resourceName}
                            <span className="text-gray-400 ml-1">
                              ({formatTime(log.total_seconds)} @ ${hourlyRate}/hr)
                            </span>
                          </span>
                          <span>-${cost.toFixed(2)}</span>
                        </div>
                      );
                    })}
                </div>
                
                {/* Show total if no breakdown available */}
                {resourceBreakdowns.length === 0 && laborCosts > 0 && (
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>• Hourly/Flat Rates</span>
                    <span>-${formatCurrency(laborCosts)}</span>
                  </div>
                )}
                
                {commissionCosts > 0 && (
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>• Commissions{isLoadingCommissions ? ' (loading...)' : ''}</span>
                    <span>-${formatCurrency(commissionCosts)}</span>
                  </div>
                )}
                {bonusCosts > 0 && (
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>• Bonuses{isLoadingBonuses ? ' (loading...)' : ''}</span>
                    <span>-${formatCurrency(bonusCosts)}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Material Costs */}
          {materialCosts > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 font-medium">Material Costs</span>
              <span className="text-red-600 font-bold">-${formatCurrency(materialCosts)}</span>
            </div>
          )}

          {/* Product Costs */}
          {(productCosts > 0 || isLoadingProductCosts) && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 font-medium">
                  Product Costs
                  {isLoadingProductCosts && ' (loading...)'}
                </span>
                <span className="text-red-600 font-bold">-${formatCurrency(productCosts)}</span>
              </div>
              
              {/* Product Cost Breakdown */}
              {products.length > 0 && (
                <div className="ml-4 space-y-1">
                  {products.map((product, index) => (
                    <div key={index} className="flex justify-between text-xs text-gray-500">
                      <span className="flex items-center">
                        • {product.product_name || product.stockItem?.name || 'Unknown Product'}
                        <span className="text-gray-400 ml-1">
                          ({product.quantity} × ${formatCurrency(product.acquisitionCost)})
                        </span>
                      </span>
                      <span>-${formatCurrency(product.totalCost)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Discounts */}
          {discounts > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 font-medium">Discounts</span>
              <span className="text-red-600 font-bold">-${formatCurrency(discounts)}</span>
            </div>
          )}

          {/* Other Costs */}
          {otherCosts > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 font-medium">Other Costs</span>
              <span className="text-red-600 font-bold">-${formatCurrency(otherCosts)}</span>
            </div>
          )}

          {/* Total Costs */}
          <div className="flex justify-between text-sm pt-3 border-t border-gray-200">
            <span className="text-gray-700 font-semibold">Total Costs</span>
            <span className="text-red-600 font-semibold">-${formatCurrency(totalCosts)}</span>
          </div>
        </div>

        {/* Net Profit Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-gray-900">Net Profit</div>
              <div className="text-xs text-gray-500">
                {profitMargin.toFixed(1)}% margin
              </div>
            </div>
            <div className="text-right">
              <div className={`text-sm font-semibold ${
                profit >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {profit >= 0 ? '+' : '-'}${formatCurrency(Math.abs(profit))}
              </div>
              <div className="text-xs text-gray-500">
                {profit >= 0 ? 'Profit' : 'Loss'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}